import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Table
} from "reactstrap";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import { toast } from "sonner";
import {
  useGetWeekendsQuery,
  useCreateWeekendMutation,
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useDeleteHolidayMutation
} from "store/features/attendance/attendanceApi";

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const HRSetupManagement = () => {
  // Weekend state
  const [selectedDays, setSelectedDays] = useState(["Friday", "Saturday"]);
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));

  // Holiday Modal state
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [holidayTitle, setHolidayTitle] = useState("");
  const [holidayStartDate, setHolidayStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [holidayEndDate, setHolidayEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [holidayType, setHolidayType] = useState("Government");
  const [holidayDesc, setHolidayDesc] = useState("");

  // RTK Query
  const { data: weekendList = [], isLoading: isWeekendLoading } = useGetWeekendsQuery();
  const { data: holidayList = [], isLoading: isHolidayLoading } = useGetHolidaysQuery();

  const [createWeekend, { isLoading: isSavingWeekend }] = useCreateWeekendMutation();
  const [createHoliday, { isLoading: isSavingHoliday }] = useCreateHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();

  useEffect(() => {
    if (weekendList.length > 0) {
      const activeWeekend = weekendList[0];
      if (activeWeekend.weekend_days && Array.isArray(activeWeekend.weekend_days)) {
        setSelectedDays(activeWeekend.weekend_days);
      }
    }
  }, [weekendList]);

  const toggleDay = day => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleSaveWeekend = async () => {
    try {
      await createWeekend({
        title: "Company Weekend Setup",
        weekend_days: selectedDays,
        is_active: true,
        effective_from: effectiveFrom
      }).unwrap();
    } catch (err) {
      toast.error("Failed to save weekend setup");
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayTitle || !holidayStartDate || !holidayEndDate) {
      toast.error("Please fill title and dates");
      return;
    }
    if (new Date(holidayStartDate) > new Date(holidayEndDate)) {
      toast.error("Start date cannot be after End date");
      return;
    }
    try {
      await createHoliday({
        title: holidayTitle,
        start_date: holidayStartDate,
        end_date: holidayEndDate,
        holiday_type: holidayType,
        description: holidayDesc
      }).unwrap();
      setHolidayModalOpen(false);
      setHolidayTitle("");
      setHolidayDesc("");
    } catch (err) {
      toast.error("Failed to save holiday");
    }
  };

  const handleDeleteHoliday = async id => {
    if (window.confirm("Are you sure you want to delete this holiday?")) {
      try {
        await deleteHoliday(id).unwrap();
      } catch (err) {
        toast.error("Failed to delete holiday");
      }
    }
  };

  if (isWeekendLoading || isHolidayLoading) return <DashboardLoader />;

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumb title="HR Management" breadcrumbItem="Weekend & Holiday Setup" />

          <Row>
            {/* WEEKEND SETUP CARD */}
            <Col lg={5}>
              <Card className="shadow-sm">
                <CardBody>
                  <h5 className="card-title fw-bold mb-3">
                    <i className="fas fa-calendar-alt me-2 text-primary" /> Weekly Off-Days Setup
                  </h5>
                  <p className="text-muted fs-7 mb-4">
                    Select which days of the week are observed as weekly holidays for all employees.
                  </p>

                  <div className="mb-4">
                    {DAYS_OF_WEEK.map(day => {
                      const isSelected = selectedDays.includes(day);
                      return (
                        <div
                          key={day}
                          className={`d-flex align-items-center justify-content-between p-3 mb-2 rounded border style={{ cursor: 'pointer' }}`}
                          style={{
                            backgroundColor: isSelected ? "#f0f4ff" : "#fff",
                            borderColor: isSelected ? "#3b82f6" : "#e5e7eb"
                          }}
                          onClick={() => toggleDay(day)}
                        >
                          <span className={`fw-bold ${isSelected ? "text-primary" : "text-dark"}`}>{day}</span>
                          <Input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // handled by div click
                          />
                        </div>
                      );
                    })}
                  </div>

                  <FormGroup className="mb-3">
                    <Label className="fw-bold">Effective From Date</Label>
                    <Input
                      type="date"
                      value={effectiveFrom}
                      onChange={e => setEffectiveFrom(e.target.value)}
                    />
                  </FormGroup>

                  <Button
                    color="primary"
                    className="w-100"
                    onClick={handleSaveWeekend}
                    disabled={isSavingWeekend}
                  >
                    <i className="fas fa-save me-2" />
                    {isSavingWeekend ? "Saving..." : "Save Weekend Setup"}
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {/* HOLIDAY LIST CARD */}
            <Col lg={7}>
              <Card className="shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title fw-bold mb-0">
                      <i className="fas fa-umbrella-beach me-2 text-success" /> Official Holidays Calendar
                    </h5>
                    <Button color="success" onClick={() => setHolidayModalOpen(true)}>
                      <i className="fas fa-plus me-2" /> Add Holiday
                    </Button>
                  </div>

                  <div className="table-responsive">
                    <Table hover striped className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Holiday Name</th>
                          <th>Type</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Description</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {holidayList.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              No holidays created yet. Click "Add Holiday" to create company holidays.
                            </td>
                          </tr>
                        ) : (
                          holidayList.map(h => (
                            <tr key={h.id}>
                              <td className="fw-bold">{h.title}</td>
                              <td>
                                <Badge color={h.holiday_type === "Government" ? "primary" : "info"}>
                                  {h.holiday_type}
                                </Badge>
                              </td>
                              <td>{h.start_date}</td>
                              <td>{h.end_date}</td>
                              <td className="text-muted">{h.description || "-"}</td>
                              <td>
                                <Button color="danger" size="sm" onClick={() => handleDeleteHoliday(h.id)}>
                                  <i className="fas fa-trash" />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ADD HOLIDAY MODAL */}
          <Modal isOpen={holidayModalOpen} toggle={() => setHolidayModalOpen(!holidayModalOpen)}>
            <ModalHeader toggle={() => setHolidayModalOpen(!holidayModalOpen)}>
              Add Official Holiday
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Holiday Title</Label>
                <Input
                  type="text"
                  placeholder="e.g. Independence Day / Eid-ul-Fitr"
                  value={holidayTitle}
                  onChange={e => setHolidayTitle(e.target.value)}
                />
              </FormGroup>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={holidayStartDate}
                      onChange={e => setHolidayStartDate(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={holidayEndDate}
                      onChange={e => setHolidayEndDate(e.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>Holiday Type</Label>
                <Input type="select" value={holidayType} onChange={e => setHolidayType(e.target.value)}>
                  <option value="Government">Government Holiday</option>
                  <option value="Company">Company Holiday</option>
                  <option value="Religious">Religious Holiday</option>
                  <option value="Other">Other</option>
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>Description / Details</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={holidayDesc}
                  onChange={e => setHolidayDesc(e.target.value)}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setHolidayModalOpen(false)}>Cancel</Button>
              <Button color="success" onClick={handleSaveHoliday} disabled={isSavingHoliday}>
                {isSavingHoliday ? "Saving..." : "Save Holiday"}
              </Button>
            </ModalFooter>
          </Modal>

        </div>
      </div>
    </React.Fragment>
  );
};

export default HRSetupManagement;
